import { useCallback, useMemo, useState, type CSSProperties } from "react";
import {
  Archive,
  Pencil,
  CirclePlus,
  RotateCcw,
  Target,
  UserRound,
} from "lucide-react";
import {
  ActionMenu,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  OwnerChip,
  Skeleton,
  initialsOf,
  enterDelay,
  relativeTime,
  toast,
  useRetainedValue,
  type ActionMenuItem,
  type DataTableColumn,
} from "@playbookai/ui";
import { useKpis } from "../hooks/useKpis.js";
import { useArchiveKpi } from "../hooks/useArchiveKpi.js";
import { useRestoreKpi } from "../hooks/useRestoreKpi.js";
import { useDefinitionNames } from "../hooks/useDefinitionNames.js";
import { useKpiActorUserId } from "../hooks/kpiAuthScope.js";
import { useKpiCategoryOptions } from "../hooks/useKpiCategoryOptions.js";
import { useOwnerNames, type KpiPersonName } from "../hooks/useOwnerNames.js";
import type { KpiListFilters, ManagedKpiSummary } from "../api/types.js";
import { AssignOwnerModal } from "./AssignOwnerModal.js";
import { errorMessage } from "./error-message.js";
import { KpiLifecyclePill } from "./KpiStatusBadges.js";
import { valueTypeLabel } from "./kpi-presentation.js";
import { KpiCard } from "./KpiCard.js";
import { KpiReadinessPill } from "./KpiReadinessPill.js";
import { RecordKpiValueModal } from "./RecordKpiValueModal.js";

export type KpiCollectionLayout = "cards" | "table";

export interface KpiListViewProps {
  readonly filters: KpiListFilters;
  readonly onSelect: (kpiId: string) => void;
  /** All KPIs can switch presentation without changing its server query. */
  readonly layout?: KpiCollectionLayout;
  /** Override whether the current query represents a user-filtered result set.
   * Useful for fixed views such as Archived, where `lifecycleStatus=archived`
   * is the collection itself rather than an applied filter. */
  readonly hasActiveFilters?: boolean;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
  /**
   * Tenant-admin governance row actions (Edit, Assign owner, Set target,
   * Archive) — PB-5134, d12. Activate stays on the KPI's own page because it
   * needs the owner and target the page shows. `Add value` is offered to the
   * KPI's OWNER regardless of this flag (ADR-0052). Defaults to no menu.
   */
  readonly canManage?: boolean;
  /** Route to the KPI's edit page; without it the menu has no Edit. */
  readonly onEdit?: (kpiId: string) => void;
  /** `archived` drops Lifecycle/Readiness and heads the date "Archived" (d35). */
  readonly variant?: "active" | "archived";
}

type RowDialog = {
  readonly kind: "assign" | "record" | "archive" | "restore";
  readonly kpi: ManagedKpiSummary;
} | null;

// Stable reference for the not-yet-loaded owner names, so the `columns` memo
// isn't invalidated by a fresh `{}` on every render during the loading window.
const EMPTY_OWNER_NAMES: Record<string, KpiPersonName> = {};

/**
 * The KPI Manager collection — a filtered, cursor-paginated card grid or
 * `DataTable` of configured KPIs. Since PB-98 the summary carries the governed
 * measurement standing too, surfaced as the Readiness pill, so a user can see
 * which KPIs are actually measurable without opening each one; the target and
 * the full three-dimension breakdown remain detail-only. Owner ids resolve to
 * names via the host's `PeopleSource`; the KPI Type name resolves from the
 * catalog.
 */
export function KpiListView({
  filters,
  onSelect,
  layout = "table",
  hasActiveFilters: hasActiveFiltersOverride,
  emptyTitle = "No KPIs yet",
  emptyDescription = "Create a KPI from a KPI template to start governing it.",
  canManage = false,
  onEdit,
  variant = "active",
}: KpiListViewProps): JSX.Element {
  const query = useKpis(filters);
  const categories = useKpiCategoryOptions();
  const categoryName = useMemo(() => {
    const byCode = new Map(
      (categories.data ?? []).map((option) => [option.code, option.name]),
    );
    return (code: string | null): string | null =>
      code ? (byCode.get(code) ?? code) : null;
  }, [categories.data]);
  const actorUserId = useKpiActorUserId();
  const archive = useArchiveKpi();
  const restore = useRestoreKpi();
  const [dialog, setDialog] = useState<RowDialog>(null);
  /* What the dialogs READ, as opposed to what decides whether they are open.
     Each overlay below stays mounted so it can animate out (ADR-0091), and on
     the closing frame `dialog` is already null while `dialog.kpi.name` is
     still on screen. The WHOLE descriptor is retained, not just the KPI:
     `kind` is what tells the three overlays apart, so keeping it is what lets
     the one that is closing go on rendering its own row while the others stay
     shut. */
  const shownDialog = useRetainedValue(dialog);

  const rows = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );
  const ownerNamesQuery = useOwnerNames(rows.map((row) => row.ownerUserId));
  const ownerNames = ownerNamesQuery.data ?? EMPTY_OWNER_NAMES;
  // Resolves catalog Types plus the compatibility/deprecated Types the catalog
  // list hides (so migration-backfilled KPIs don't render their Type as "—").
  const definitionName = useDefinitionNames(
    rows.map((row) => row.definitionId),
  );

  // One menu per row, from the artboard's row actions (d12). Empty for a
  // member who owns nothing here, in which case no trigger renders.
  const rowActions = useMemo(
    () =>
      (row: ManagedKpiSummary): readonly ActionMenuItem[] => {
        const archived = row.lifecycleStatus === "archived";
        if (archived) {
          return canManage
            ? [
                {
                  id: "restore",
                  label: "Restore",
                  icon: RotateCcw,
                  onSelect: () => setDialog({ kind: "restore", kpi: row }),
                },
              ]
            : [];
        }
        const owner =
          actorUserId !== null &&
          row.ownerUserId !== null &&
          actorUserId === row.ownerUserId;
        const items: ActionMenuItem[] = [];
        if (canManage && onEdit) {
          items.push({
            id: "edit",
            label: "Edit",
            icon: Pencil,
            onSelect: () => onEdit(row.kpiId),
          });
        }
        if (canManage) {
          items.push(
            {
              id: "assign-owner",
              label: "Assign owner",
              icon: UserRound,
              onSelect: () => setDialog({ kind: "assign", kpi: row }),
            },
            {
              id: "set-target",
              label: "Set target",
              icon: Target,
              // Target is detail-only because that read carries the singular
              // currentTarget + stable ttiId. Opening a blind list modal would
              // be ambiguous when the backend holds several TTIs for this KPI.
              onSelect: () => onSelect(row.kpiId),
            },
          );
        }
        if (owner) {
          items.push({
            id: "add-value",
            label: "Add value",
            icon: CirclePlus,
            onSelect: () => setDialog({ kind: "record", kpi: row }),
          });
        }
        if (canManage) {
          items.push({
            id: "archive",
            label: "Archive",
            icon: Archive,
            tone: "critical",
            separatorBefore: true,
            onSelect: () => setDialog({ kind: "archive", kpi: row }),
          });
        }
        return items;
      },
    [actorUserId, canManage, onEdit, onSelect],
  );
  const rowMenu = useCallback(
    (row: ManagedKpiSummary): JSX.Element | null => {
      const items = rowActions(row);
      return items.length ? (
        <ActionMenu items={items} triggerLabel={`Actions for ${row.name}`} />
      ) : null;
    },
    [rowActions],
  );

  const columns = useMemo<
    ReadonlyArray<DataTableColumn<ManagedKpiSummary>>
  >(() => {
    const all: ReadonlyArray<DataTableColumn<ManagedKpiSummary>> = [
      {
        id: "name",
        header: "KPI",
        sortable: true,
        sortAccessor: (row) => row.name,
        cardRole: "title",
        // Kept hand-built (not Button): Button is a boxed, sized control;
        // this is a plain inline text trigger with an underline hover — see
        // KpiPanel.tsx for the same reasoning.
        cell: (row) => (
          <button
            type="button"
            onClick={() => onSelect(row.kpiId)}
            className="rounded-sm text-left font-medium text-pb-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal"
            aria-label={`Open KPI: ${row.name}`}
          >
            {row.name}
          </button>
        ),
      },
      {
        id: "type",
        header: "Type",
        cardRole: "subtitle",
        cell: (row) => (
          <span
            className="text-pb-text-secondary"
            title={`${definitionName(row.definitionId)} · ${row.unit}`}
          >
            {valueTypeLabel(row.unit)}
          </span>
        ),
      },
      {
        id: "owner",
        header: "Owner",
        cardRole: "meta",
        cell: (row) => {
          if (!row.ownerUserId) {
            return <span className="text-pb-text-muted">Unassigned</span>;
          }
          const owner = ownerNames[row.ownerUserId];
          return owner ? (
            <span className="inline-flex items-center gap-1.5">
              <OwnerChip
                initials={initialsOf(owner.name)}
                name={owner.name}
                seed={row.ownerUserId}
              />
              {owner.active ? null : (
                <Badge shape="pill" size="sm" tone="watch" variant="soft">
                  Not active
                </Badge>
              )}
            </span>
          ) : (
            <span className="text-pb-text-muted">
              {row.ownerUserId.slice(0, 8)}…
            </span>
          );
        },
      },
      {
        id: "lifecycle",
        header: "Lifecycle",
        cardRole: "badge",
        sortable: true,
        sortAccessor: (row) => row.lifecycleStatus,
        cell: (row) => <KpiLifecyclePill status={row.lifecycleStatus} />,
      },
      {
        id: "readiness",
        header: "Readiness",
        cardRole: "badge",
        sortable: true,
        sortAccessor: (row) => row.readiness,
        cell: (row) => <KpiReadinessPill readiness={row.readiness} />,
      },
      {
        id: "updated",
        header: "Updated",
        align: "end",
        cardRole: "meta",
        sortable: true,
        sortAccessor: (row) => row.updatedAt,
        cell: (row) => (
          <span className="tabular-nums text-pb-text-muted">
            {relativeTime(row.updatedAt)}
          </span>
        ),
      },
      {
        id: "actions",
        // Named for assistive tech, not shown: the trigger in each row carries
        // the row's own name.
        header: <span className="sr-only">Actions</span>,
        align: "end",
        cardRole: "meta",
        cell: (row) => rowMenu(row),
      },
    ];
    if (variant !== "archived") return all;
    return all
      .filter(
        (column) => column.id !== "lifecycle" && column.id !== "readiness",
      )
      .map((column) =>
        column.id === "updated" ? { ...column, header: "Archived" } : column,
      );
  }, [definitionName, onSelect, ownerNames, rowMenu, variant]);

  const handleArchive = (kpi: ManagedKpiSummary): void => {
    archive.mutate(kpi.kpiId, {
      onSuccess: () => {
        setDialog(null);
        toast("KPI archived", { tone: "neutral" });
      },
      onError: (err) =>
        toast(errorMessage(err, "Could not archive the KPI."), {
          tone: "critical",
        }),
    });
  };

  const handleRestore = (kpi: ManagedKpiSummary): void => {
    restore.mutate(kpi.kpiId, {
      onSuccess: () => {
        setDialog(null);
        toast("KPI restored to draft", { tone: "healthy" });
      },
      onError: (err) =>
        toast(errorMessage(err, "Could not restore the KPI."), {
          tone: "critical",
        }),
    });
  };

  if (query.isError) {
    return (
      <EmptyState
        variant="error"
        title="KPIs could not be loaded"
        description="The KPI list is temporarily unavailable. Try the request again."
        action={<Button onClick={() => void query.refetch()}>Retry</Button>}
      />
    );
  }

  const hasActiveFilters =
    hasActiveFiltersOverride ??
    Boolean(
      filters.q ||
      filters.lifecycleStatus ||
      filters.mine ||
      filters.definitionId ||
      filters.ownerUserIds?.length,
    );
  const emptyState = hasActiveFilters ? (
    <EmptyState
      variant="no-results"
      title="No KPIs match these filters"
      description="Adjust the search or filters to see more."
    />
  ) : (
    <EmptyState
      variant="first-use"
      title={emptyTitle}
      description={emptyDescription}
    />
  );

  return (
    // `aria-busy` on the COLLECTION, not on one layout's skeletons: both the
    // card grid and the table render placeholder rows while the first page is
    // in flight, and a screen reader acts on `aria-busy` — a label alone just
    // names a region that then sits silently in front of content that is not
    // there. The detail and edit pages already mark their own skeletons this
    // way; the collection is the surface people land on first and was the one
    // that did not.
    <div className="space-y-4" aria-busy={query.isLoading}>
      {layout === "cards" ? (
        query.isLoading ? (
          <div
            role="status"
            aria-label="Loading KPIs"
            className="grid grid-cols-1 gap-4 @2xl/content:grid-cols-2 @4xl/content:grid-cols-3"
          >
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-52" shape="card" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          emptyState
        ) : (
          <div className="grid grid-cols-1 gap-4 @2xl/content:grid-cols-2 @4xl/content:grid-cols-3">
            {rows.map((row, index) => (
              /* The arrival grade goes on a wrapper, not on the card: an
                 interactive `Card` already carries `press`, which owns a
                 transform transition that `pb-enter-row` would take over
                 (ADR-0091). The wrapper is the grid item; the card fills it. */
              <div
                key={row.kpiId}
                className="pb-enter-row"
                style={enterDelay(index) as CSSProperties}
              >
                <KpiCard
                  kpi={row}
                  definitionName={definitionName(row.definitionId)}
                  categoryName={categoryName(row.categoryCode)}
                  {...(row.ownerUserId && ownerNames[row.ownerUserId]
                    ? { owner: ownerNames[row.ownerUserId] }
                    : {})}
                  onOpen={() => onSelect(row.kpiId)}
                  actions={rowMenu(row)}
                />
              </div>
            ))}
          </div>
        )
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => row.kpiId}
          density="compact"
          caption="Configured KPIs"
          responsive
          loading={query.isLoading}
          emptyState={emptyState}
        />
      )}
      {!query.isLoading && rows.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2.5 border-t border-pb-border/50 px-1 pt-2.75 text-caption text-pb-text-muted">
          <span>
            {variant === "archived"
              ? `${rows.length} archived ${rows.length === 1 ? "KPI" : "KPIs"}`
              : `Showing ${rows.length} ${rows.length === 1 ? "KPI" : "KPIs"}${query.hasNextPage ? " so far" : ""}`}
          </span>
          {query.hasNextPage ? (
            <Button
              variant="link"
              size="sm"
              onClick={() => void query.fetchNextPage()}
              loading={query.isFetchingNextPage}
            >
              Load more
            </Button>
          ) : null}
        </div>
      ) : null}

      <AssignOwnerModal
        kpiId={shownDialog?.kpi.kpiId ?? ""}
        open={dialog?.kind === "assign"}
        onClose={() => setDialog(null)}
      />
      <RecordKpiValueModal
        kpiId={shownDialog?.kpi.kpiId ?? ""}
        unit={shownDialog?.kpi.unit ?? ""}
        open={dialog?.kind === "record"}
        onClose={() => setDialog(null)}
      />
      <ConfirmDialog
        open={dialog?.kind === "archive" || dialog?.kind === "restore"}
        onClose={() => setDialog(null)}
        tone={shownDialog?.kind === "restore" ? "neutral" : "critical"}
        title={
          shownDialog?.kind === "archive"
            ? `Archive ${shownDialog.kpi.name}?`
            : shownDialog?.kind === "restore"
              ? `Restore ${shownDialog.kpi.name}?`
              : ""
        }
        cancelLabel={shownDialog?.kind === "restore" ? "Cancel" : "Keep"}
        confirmLabel={shownDialog?.kind === "restore" ? "Restore" : "Archive"}
        loading={
          shownDialog?.kind === "restore"
            ? restore.isPending
            : archive.isPending
        }
        /* The ACT reads `dialog`, never the retained copy: a press that
           somehow landed during the closing frame must do nothing, not
           archive the row a second time. */
        onConfirm={() => {
          if (dialog?.kind === "archive") handleArchive(dialog.kpi);
          if (dialog?.kind === "restore") handleRestore(dialog.kpi);
        }}
      >
        {shownDialog?.kind === "restore"
          ? "This KPI will return to Draft. Its owner, target, and value history will be kept."
          : "An archived KPI records no further values and moves to Archived, where its history stays readable."}
      </ConfirmDialog>
    </div>
  );
}
